import { isPlatformBrowser } from '@angular/common';
import {
  inject,
  Injectable,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
} from '@angular/core';

export type Theme = string; // Change to allow dynamic theme strings

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly rendererFactory = inject(RendererFactory2);
  renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);
  themeLinkElementId = 'app-theme'; // ID for the dynamic theme link element

  currentTheme: string = ''; // Default theme

  private normalizeThemeUrl(themeUrlOrName: string): string {
    if (!themeUrlOrName) return '';
    let path = themeUrlOrName.trim();
    if (!path.endsWith('.css') && !path.includes('.')) {
      path = `${path}.css`;
    }
    if (
      !path.startsWith('assets/') &&
      !path.startsWith('/assets/') &&
      !path.startsWith('http://') &&
      !path.startsWith('https://')
    ) {
      path = `assets/themes/B2B/${path}`;
    }
    return path;
  }

  loadThemeStylesheet(themeURL: string): void {
    if (!isPlatformBrowser(this.platformId) || !themeURL) {
      return;
    }

    const normalizedUrl = this.normalizeThemeUrl(themeURL);
    const head = document.head;
    if (!head) return;

    const existingLinkElement = document.getElementById(
      this.themeLinkElementId,
    ) as HTMLLinkElement | null;

    if (existingLinkElement) {
      const currentHref = existingLinkElement.getAttribute('href');
      if (currentHref === normalizedUrl) {
        return;
      }
      existingLinkElement.remove();
    }

    const link = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'id', this.themeLinkElementId);
    this.renderer.setAttribute(link, 'rel', 'stylesheet');
    this.renderer.setAttribute(link, 'href', normalizedUrl);
    this.renderer.appendChild(head, link);
  }

  removeExistingTheme(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const existingLinkElement = document.getElementById(
      this.themeLinkElementId,
    );
    if (existingLinkElement) {
      existingLinkElement.remove();
    }
    this.currentTheme = '';
  }

  setTheme(theme: Theme, themeURL: string): void {
    if (!themeURL) {
      this.removeExistingTheme();
      return;
    }
    this.loadThemeStylesheet(themeURL);
    this.currentTheme = theme;
  }
}
